import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService, User } from './services/auth';
import { FeatureFlagService } from './services/feature-flag';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DomainContextService } from './services/domain-context';
import { CompareContextService, CompareSettingsResponse } from './services/compare-context';
import { CompareDomainContextService } from './services/compare-domain-context';
import { GlobalNotificationStackComponent } from './components/global-notification-stack/global-notification-stack';

// Note: Routing is now provided in main.ts using provideRouter

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    GlobalNotificationStackComponent
  ],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App implements OnInit, OnDestroy {
  currentUser: User | null = null;
  /** True when the current route is a public share page (/share/:token) */
  isShareRoute = false;
  availableDomains: string[] = [];
  selectedDomain = '';
  isCompetitorMenuOpen = false;
  markedCompetitors: string[] = [];
  selectedCompareDomain: string | null = null;
  newCompetitorInput = '';
  isLoadingCompareSettings = false;
  isSavingCompetitor = false;
  isSavingCompareSelection = false;
  compareError = '';
  private compareSettingsRequestGen = 0;
  private compareMutationRequestGen = 0;

  private readonly subscriptions = new Subscription();

  constructor(
    public authService: AuthService,
    private router: Router,
    public featureFlags: FeatureFlagService,
    private domainContext: DomainContextService,
    private compareContext: CompareContextService,
    private compareDomainContext: CompareDomainContextService
  ) {}

  ngOnInit() {
    // Track whether we're on a public share route (hide app chrome)
    this.isShareRoute = this.router.url.startsWith('/share/');
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.isShareRoute = e.urlAfterRedirects.startsWith('/share/');
    });

    this.subscriptions.add(this.domainContext.domains$.subscribe((domains) => {
      this.availableDomains = domains;
    }));

    this.subscriptions.add(this.domainContext.selectedDomain$.subscribe((domain) => {
      this.selectedDomain = domain;
      this.loadCompareSettings();
    }));

    this.subscriptions.add(this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      if (user) {
        this.domainContext.ensureInitialized();
      } else {
        this.compareSettingsRequestGen++;
        this.compareMutationRequestGen++;
        this.domainContext.reset();
        this.compareDomainContext.reset();
        this.markedCompetitors = [];
        this.selectedCompareDomain = null;
        this.newCompetitorInput = '';
        this.compareError = '';
        this.isLoadingCompareSettings = false;
        this.isSavingCompetitor = false;
        this.isSavingCompareSelection = false;
        this.isCompetitorMenuOpen = false;
      }

      const path = window.location.pathname;
      // Only the bare root. `router.url` is '/' on every path until the first navigation
      // ends, which raced AuthGuard and dropped its "session expired" notice (§0cr).
      if (!user && path === '/' && !this.authService.hasExpiredSession()) {
        this.router.navigate(['/login']);
      } else if (user && this.router.url === '/login') {
        this.router.navigate(['/dashboard']);
      }
    }));

    this.subscriptions.add(this.compareDomainContext.selectedCompareDomain$.subscribe((domain) => {
      this.selectedCompareDomain = domain;
    }));
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  toggleDevUserView() {
    this.featureFlags.toggleUserView();
  }

  onDomainChange(domain: string) {
    this.isCompetitorMenuOpen = false;
    this.newCompetitorInput = '';
    this.compareError = '';
    this.domainContext.setSelectedDomain(domain);
  }

  get competitorSummaryText(): string {
    const count = this.markedCompetitors.length;
    return `${count} competitor${count === 1 ? '' : 's'} configured`;
  }

  toggleCompetitorMenu() {
    this.isCompetitorMenuOpen = !this.isCompetitorMenuOpen;
  }

  addCompetitor() {
    if (this.isSavingCompetitor) {
      return;
    }

    const domain = this.newCompetitorInput.trim();
    if (!domain) {
      this.compareError = 'Enter a competitor domain.';
      return;
    }

    this.isSavingCompetitor = true;
    this.compareError = '';
    const requestDomain = this.selectedDomain.trim();
    const requestGen = ++this.compareMutationRequestGen;

    this.compareContext.addMarkedCompetitor(domain, requestDomain || undefined).subscribe({
      next: (response) => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.applyCompareResponse(response);
        this.newCompetitorInput = '';
      },
      error: (error) => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.compareError = this.extractCompareError(error, 'Failed to add competitor domain.');
        this.isSavingCompetitor = false;
      },
      complete: () => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.isSavingCompetitor = false;
      }
    });
  }

  removeCompetitor(domain: string) {
    if (this.isSavingCompetitor) {
      return;
    }

    this.isSavingCompetitor = true;
    this.compareError = '';
    const requestDomain = this.selectedDomain.trim();
    const requestGen = ++this.compareMutationRequestGen;

    this.compareContext.removeMarkedCompetitor(domain, requestDomain || undefined).subscribe({
      next: (response) => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.applyCompareResponse(response);
      },
      error: (error) => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.compareError = this.extractCompareError(error, 'Failed to remove competitor domain.');
        this.isSavingCompetitor = false;
      },
      complete: () => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.isSavingCompetitor = false;
      }
    });
  }

  private loadCompareSettings() {
    if (!this.currentUser) {
      return;
    }

    const activeDomain = this.selectedDomain.trim();
    if (!activeDomain) {
      this.compareSettingsRequestGen++;
      this.markedCompetitors = [];
      this.compareDomainContext.reset();
      this.compareError = '';
      return;
    }

    const requestGen = ++this.compareSettingsRequestGen;
    const requestDomain = activeDomain;
    this.isLoadingCompareSettings = true;
    this.compareError = '';

    this.compareContext.getSettings(activeDomain).subscribe({
      next: (response) => {
        if (requestGen !== this.compareSettingsRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.applyCompareResponse(response);
      },
      error: (error) => {
        if (requestGen !== this.compareSettingsRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.compareError = this.extractCompareError(error, 'Failed to load competitor settings.');
        this.isLoadingCompareSettings = false;
      },
      complete: () => {
        if (requestGen !== this.compareSettingsRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.isLoadingCompareSettings = false;
      }
    });
  }

  private applyCompareResponse(response: CompareSettingsResponse) {
    const domains = response?.data?.markedCompetitorDomains;
    this.markedCompetitors = Array.isArray(domains) ? domains : [];
    this.compareDomainContext.setSelectedCompareDomain(response?.data?.selectedCompareDomain || null);
    this.compareError = '';
  }

  isComparedDomain(domain: string): boolean {
    return this.normalizeDomain(domain) === this.selectedCompareDomain;
  }

  toggleCompareCompetitor(domain: string) {
    if (this.isSavingCompareSelection) {
      return;
    }

    if (!this.selectedDomain) {
      this.compareDomainContext.setSelectedCompareDomain(null);
      return;
    }

    const normalized = this.normalizeDomain(domain);
    if (!normalized) {
      return;
    }

    const selectedCompareDomain = this.selectedCompareDomain === normalized ? null : normalized;
    this.persistCompareSelection(selectedCompareDomain);
  }

  private persistCompareSelection(selectedCompareDomain: string | null) {
    const requestDomain = this.selectedDomain.trim();
    if (!requestDomain) {
      this.compareDomainContext.setSelectedCompareDomain(null);
      return;
    }
    const requestGen = ++this.compareMutationRequestGen;
    this.isSavingCompareSelection = true;
    this.compareError = '';

    this.compareContext.updateSettings({
      activeDomain: requestDomain,
      selectedCompareDomain
    }).subscribe({
      next: (response) => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.applyCompareResponse(response);
      },
      error: (error) => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.compareError = this.extractCompareError(error, 'Failed to update compare domain.');
        this.isSavingCompareSelection = false;
      },
      complete: () => {
        if (requestGen !== this.compareMutationRequestGen || this.selectedDomain.trim() !== requestDomain) {
          return;
        }
        this.isSavingCompareSelection = false;
      }
    });
  }

  private extractCompareError(error: any, fallback: string): string {
    const message = error?.error?.message;
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    return fallback;
  }

  private normalizeDomain(value: string | null | undefined): string | null {
    const normalized = (value || '')
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
      .split('?')[0]
      .split('#')[0]
      .split(':')[0]
      .trim();

    return normalized || null;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

}
